/**
 * Mycelium Database-Change Connector
 *
 * Phase 1 of the Realtime ↔ Mycelium bridge. Subscribes to row-change events
 * emitted by the server-side Postgres triggers installed in
 * `20260417000000_mycelium_row_change_bridge.sql`. Its job is to:
 *
 *   1. **Centralise** — every row-change event flows through here first,
 *      providing a single place to add cross-cutting concerns (metrics,
 *      redaction, sampling, backpressure) without touching individual domain
 *      connectors.
 *   2. **De-duplicate at the edge** — keeps a small recent-seen LRU of
 *      `dedupKey`s so the same row bouncing through multiple transports
 *      (PGMQ, in-memory, Realtime echo) is only observed once.
 *   3. **Normalise** — exposes stats by table/op for observability. Downstream
 *      domain connectors still subscribe to their natural domains (task,
 *      accounting, etc.) and receive the events via the backbone as usual.
 *
 * The connector is a **read-only observer**: it never republishes events, so
 * it cannot create feedback loops. Domain-specific reactions (e.g. awarding
 * XP on task_created) live in the existing connectors.
 *
 * @module mycelium-database-change.connector
 * @since 2026-04-17
 */

import { ROW_CHANGE_EVENT_TYPES } from "@flowstate/mycelium-core";
import type { MyceliumEvent, EventDomain } from "./mycelium-event-backbone.service";
import { myceliumEventBackbone } from "./mycelium-event-backbone.service";
import { logger } from "../lib/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Maximum number of recent dedup keys to keep in memory. */
const DEDUP_CACHE_SIZE = 2048;

/** Domains this connector observes. Matches the Phase-1 trigger installation. */
const SUBSCRIBED_DOMAINS: readonly EventDomain[] = [
  "task",
  "notification",
  "crm",
  "accounting",
  "ai",
  "system",
] as const;

/** Event types this connector actually cares about — all from row-change.events. */
const ROW_CHANGE_TYPES = new Set<string>(ROW_CHANGE_EVENT_TYPES);

// ============================================================================
// TYPES
// ============================================================================

interface ConnectorStats {
  /** Total events observed (post-dedup). */
  observed: number;
  /** Duplicate events short-circuited by dedup cache. */
  deduped: number;
  /** Non-row-change events that reached the handler (should be zero). */
  filteredOut: number;
  /** Handler errors. */
  errors: number;
  /** Count by `{table}:{op}`. */
  byTableOp: Record<string, number>;
  /** Count by event type. */
  byType: Record<string, number>;
}

// ============================================================================
// CONNECTOR
// ============================================================================

export class DatabaseChangeConnector {
  private _initialized = false;
  private static readonly SUBSCRIBER_NAME = "DatabaseChangeConnector";
  private dedupCache = new Set<string>();
  private dedupOrder: string[] = [];
  private stats: ConnectorStats = {
    observed: 0,
    deduped: 0,
    filteredOut: 0,
    errors: 0,
    byTableOp: {},
    byType: {},
  };

  get initialized(): boolean {
    return this._initialized;
  }

  async initialize(): Promise<void> {
    if (this._initialized) {
      logger.debug("DatabaseChangeConnector already initialized");
      return;
    }

    logger.info("🧬 DatabaseChange connector initializing", {
      subscribedDomains: SUBSCRIBED_DOMAINS,
      dedupCacheSize: DEDUP_CACHE_SIZE,
    });

    myceliumEventBackbone.subscribe({
      name: DatabaseChangeConnector.SUBSCRIBER_NAME,
      domains: SUBSCRIBED_DOMAINS as EventDomain[],
      handler: (event) => this.handleEvent(event),
      // Observer priority — run after business logic, before audit trail.
      priority: 2,
    });

    this._initialized = true;
    logger.info("✅ DatabaseChange connector ready");
  }

  async dispose(): Promise<void> {
    if (!this._initialized) return;

    try {
      myceliumEventBackbone.unsubscribe(DatabaseChangeConnector.SUBSCRIBER_NAME);
    } catch (err) {
      logger.warn("DatabaseChange connector unsubscribe failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    this.dedupCache.clear();
    this.dedupOrder = [];
    this._initialized = false;

    logger.info("🧬 DatabaseChange connector disposed", { stats: this.stats });
  }

  /**
   * Get a snapshot of the current stats. Useful for dashboards and tests.
   */
  getStats(): ConnectorStats {
    return {
      ...this.stats,
      byTableOp: { ...this.stats.byTableOp },
      byType: { ...this.stats.byType },
    };
  }

  // ── Internals ─────────────────────────────────────────────────────────

  /**
   * Extract the dedup key from an event. Looks first at the payload (where
   * the trigger writes it) and falls back to metadata for completeness.
   * Returns undefined when no dedup key is present.
   */
  private extractDedupKey(event: MyceliumEvent): string | undefined {
    const payload = event.payload;
    if (payload && typeof payload === "object") {
      const candidate = (payload as Record<string, unknown>).dedupKey;
      if (typeof candidate === "string" && candidate.length > 0) {
        return candidate;
      }
    }
    const metaCandidate = (event.metadata as unknown as Record<string, unknown> | undefined)?.dedupKey;
    return typeof metaCandidate === "string" && metaCandidate.length > 0 ? metaCandidate : undefined;
  }

  /**
   * Narrow the raw event payload to the fields we care about. We deliberately
   * avoid a hard `safeParse` here because (a) downstream validation already
   * happens in the worker via the mycelium-core discriminated union, and
   * (b) this connector must never drop events just because a producer adds
   * a new optional field — it's an observer, not an enforcer.
   */
  private readRowChangeFields(
    payload: unknown,
  ): { op?: string; table?: string; id?: string; organizationId?: string | null } {
    if (!payload || typeof payload !== "object") return {};
    const p = payload as Record<string, unknown>;
    return {
      op: typeof p.op === "string" ? p.op : undefined,
      table: typeof p.table === "string" ? p.table : undefined,
      id: typeof p.id === "string" ? p.id : undefined,
      organizationId:
        typeof p.organizationId === "string"
          ? p.organizationId
          : p.organizationId === null
          ? null
          : undefined,
    };
  }

  private async handleEvent(event: MyceliumEvent): Promise<void> {
    try {
      if (!ROW_CHANGE_TYPES.has(event.type)) {
        // Not our concern — the backbone delivers every event in the
        // subscribed domains, but we only process row-change types.
        this.stats.filteredOut++;
        return;
      }

      const dedupKey = this.extractDedupKey(event);

      if (dedupKey && this.dedupCache.has(dedupKey)) {
        this.stats.deduped++;
        logger.debug("🧬 row-change deduped", {
          eventId: event.id,
          type: event.type,
          dedupKey,
        });
        return;
      }

      if (dedupKey) {
        this.rememberDedupKey(dedupKey);
      }

      const fields = this.readRowChangeFields(event.payload);

      this.stats.observed++;
      this.stats.byType[event.type] = (this.stats.byType[event.type] ?? 0) + 1;

      const table = fields.table ?? "unknown";
      const op = fields.op ?? "unknown";
      const key = `${table}:${op}`;
      this.stats.byTableOp[key] = (this.stats.byTableOp[key] ?? 0) + 1;

      logger.debug("🧬 row-change observed", {
        eventId: event.id,
        domain: event.domain,
        type: event.type,
        table,
        op,
        rowId: fields.id,
        orgId: event.orgId ?? fields.organizationId ?? null,
      });
    } catch (error) {
      this.stats.errors++;
      logger.error("❌ DatabaseChangeConnector handler error", {
        eventId: event.id,
        eventType: event.type,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private rememberDedupKey(key: string): void {
    this.dedupCache.add(key);
    this.dedupOrder.push(key);
    // Bounded LRU-ish eviction (FIFO approximation — good enough for dedup).
    // `push` adds exactly one key per call, so the array can exceed
    // `DEDUP_CACHE_SIZE` by at most 1 — `if` suffices, no need for `while`.
    if (this.dedupOrder.length > DEDUP_CACHE_SIZE) {
      const evicted = this.dedupOrder.shift();
      if (evicted !== undefined) {
        this.dedupCache.delete(evicted);
      }
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const databaseChangeConnector = new DatabaseChangeConnector();
